import type { JSX } from 'react';
import { ActionIcon, Menu, Text, Tooltip } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { todayIso } from '../format';
import { sheetsFromPage } from './pageTables';
import { downloadXlsx } from './xlsx';
import { linkTo } from '../ui/links';
import { useDownloads } from './downloads';
import { printOn } from './paper';

/** The page's own heading, for file and sheet names. */
function pageTitle(): string {
  const main = document.querySelector('main');
  const project = main?.querySelector('[title="Projektnamen ändern"], h3')?.textContent?.trim();
  const heading = main?.querySelector('h2, h3, h4, h5')?.textContent?.trim();
  return (project || heading || 'PST').slice(0, 60);
}

/**
 * The one download button, in the header (AP-100): first the files the page
 * shown registers (usePageDownloads), then what every page has - PDF through
 * the browser's print on A4 or A3, Excel from the page's tables, and the
 * Mappe for several sheets at once.
 */
export function ExportMenu(): JSX.Element {
  const navigate = useNavigate();
  const pageFiles = useDownloads();
  const excel = (): void => {
    const main = document.querySelector('main');
    if (!main) return;
    const title = pageTitle();
    const sheets = sheetsFromPage(main as HTMLElement, title);
    if (sheets.length === 0) {
      window.alert('Auf dieser Seite steht keine Tabelle. Für mehrere Blätter aus Projekten: „Mappe zusammenstellen“.');
      return;
    }
    downloadXlsx(sheets, `${title.replace(/[^\p{L}\p{N} ._-]+/gu, ' ').trim()} ${todayIso()}.xlsx`);
  };
  return (
    <Menu position="bottom-end" width={300}>
      <Menu.Target>
        <Tooltip label="Herunterladen: Dateien dieser Seite, PDF, Excel, Mappe">
          <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Herunterladen">⤓</ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        {pageFiles.length > 0 && (
          <>
            <Menu.Label>Dateien dieser Seite</Menu.Label>
            {pageFiles.map((d) => (
              <Menu.Item key={d.key} {...(d.href ? { component: 'a' as const, href: d.href, download: true } : d.to ? { component: 'a' as const, ...linkTo(navigate, d.to) } : { onClick: d.onClick })}>
                <Text size="sm">{d.label}</Text>
                {d.hint && <Text size="xs" c="dimmed">{d.hint}</Text>}
              </Menu.Item>
            ))}
            <Menu.Divider />
          </>
        )}
        <Menu.Label>Diese Seite</Menu.Label>
        <Menu.Item onClick={() => printOn('A4')}>
          <Text size="sm">Als PDF, A4 quer</Text>
          <Text size="xs" c="dimmed">Drucken → „Als PDF speichern“</Text>
        </Menu.Item>
        <Menu.Item onClick={() => printOn('A3')}>
          <Text size="sm">Als PDF, A3 quer</Text>
          <Text size="xs" c="dimmed">für breite Tabellen und Pläne</Text>
        </Menu.Item>
        <Menu.Item onClick={excel}>
          <Text size="sm">Als Excel (xlsx)</Text>
          <Text size="xs" c="dimmed">jede Tabelle der Seite ein Blatt, Zahlen rechenbar</Text>
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item component="a" {...linkTo(navigate, '/mappe')}>
          <Text size="sm">Mappe zusammenstellen …</Text>
          <Text size="xs" c="dimmed">mehrere Blätter aus Projekten in einer Datei</Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
